import * as Sentry from '@sentry/react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FeedbackToolCopy, FeedbackToolProps } from 'ComponentLibrary/src/molecules/FeedbackTool/FeedbackTool'
import { joinDefinedStrings } from 'ComponentLibrary'

import { useIamContext } from 'ClientPortal/src/context/IAmContext'

const SENTRY_FEEDBACK_FLUSH_TIMEOUT_IN_MS = 2000

const useFeedbackHandler = () => {
    const [eventId, setEventId] = useState<string | undefined>(undefined)
    const { user } = useIamContext()
    const { t } = useTranslation('pages', { keyPrefix: 'playground.shared.feedback.payload' })

    const onOpenFeedbackModal = useCallback(() => {
        if (!Sentry.isInitialized()) {
            setEventId(undefined)
            return
        }

        const nextEventId = Sentry.captureMessage('User Feedback')
        setEventId(nextEventId)
    }, [])

    const onSubmitFeedback = useCallback(
        async ({ category, feedback, isIncident }: { category: string; feedback: string; isIncident: boolean }) => {
            if (!Sentry.isInitialized()) {
                throw new Error('Sentry is not initialized')
            }

            Sentry.captureFeedback({
                message: `${t('category')}: ${isIncident ? `${t('incident')}-` : ''}${category} \n\n${feedback}`,
                name: joinDefinedStrings([user?.first_name, user?.last_name]),
                email: user?.email || '',
                associatedEventId: eventId,
            })

            const didFlush = await Sentry.flush(SENTRY_FEEDBACK_FLUSH_TIMEOUT_IN_MS)
            if (!didFlush) {
                throw new Error('Sentry feedback could not be sent')
            }
        },
        [eventId, t, user]
    )

    return { onOpenFeedbackModal, onSubmitFeedback }
}

export const usePlaygroundFeedback = (): { feedbackButton: FeedbackToolProps; openFeedback: () => void } => {
    const [isOpen, setIsOpen] = useState(false)
    const { onOpenFeedbackModal, onSubmitFeedback } = useFeedbackHandler()
    const { t } = useTranslation('pages', { keyPrefix: 'playground.shared.feedback.tool' })

    const feedbackToolCopy: FeedbackToolCopy = useMemo(
        () => ({
            triggerLabel: t('triggerLabel'),
            tooltip: t('tooltip'),
            title: t('title'),
            close: t('close'),
            cancel: t('cancel'),
            submit: t('submit'),
            categoryLabel: t('categoryLabel'),
            descriptionLabel: t('descriptionLabel'),
            descriptionTooltip: t('descriptionTooltip'),
            descriptionFieldLabel: t('descriptionFieldLabel'),
            descriptionAriaLabel: t('descriptionAriaLabel'),
            incidentLabel: t('incidentLabel'),
            incidentTooltip: t('incidentTooltip'),
            errorMessage: t('errorMessage'),
            successMessage: t('successMessage'),
            options: t('options', { returnObjects: true }) as FeedbackToolCopy['options'],
        }),
        [t]
    )

    const openFeedback = useCallback(() => {
        onOpenFeedbackModal()
        setIsOpen(true)
    }, [onOpenFeedbackModal])

    const closeFeedback = useCallback(() => {
        setIsOpen(false)
    }, [])

    return {
        openFeedback,
        feedbackButton: {
            isOpen,
            onOpen: openFeedback,
            onClose: closeFeedback,
            sendFeedback: onSubmitFeedback,
            showIncidentOption: false,
            copy: feedbackToolCopy,
        },
    }
}
